import { lazy } from 'react';
import { Routes, Route } from 'react-router-dom';
import { LazyPage } from './lazy';
import { ProtectedRoute, AdminRoute } from './ProtectedRoute';
import { RouteErrorBoundary } from './ErrorBoundary';

const Home = lazy(() => import('../pages/Home'));
const Programs = lazy(() => import('../pages/Programs'));
const ProgramDetail = lazy(() => import('../pages/Programs/Detail'));
const About = lazy(() => import('../pages/About'));
const FAQ = lazy(() => import('../pages/FAQ'));
const NotFound = lazy(() => import('../pages/NotFound'));
const Login = lazy(() => import('../pages/Auth/Login'));
const Signup = lazy(() => import('../pages/Auth/Signup'));
const ForgotPassword = lazy(() => import('../pages/Auth/ForgotPassword'));
const PaymentSuccess = lazy(() => import('../pages/PaymentSuccess'));

const Dashboard = lazy(() => import('../pages/Dashboard'));
const DashboardHome = lazy(() => import('../pages/Dashboard/Home'));
const DashboardChildren = lazy(() => import('../pages/Dashboard/Children'));
const DashboardEnrollments = lazy(
  () => import('../pages/Dashboard/Enrollments')
);
const DashboardOrders = lazy(() => import('../pages/Dashboard/Orders'));
const DashboardLessons = lazy(() => import('../pages/Dashboard/Lessons'));
const DashboardLessonList = lazy(
  () => import('../pages/Dashboard/LessonList')
);
const DashboardLessonViewer = lazy(
  () => import('../pages/Dashboard/LessonViewer')
);

const Admin = lazy(() => import('../pages/Admin'));
const AdminHome = lazy(() => import('../pages/Admin/Home'));
const AdminUsers = lazy(() => import('../pages/Admin/Users'));
const AdminPrograms = lazy(() => import('../pages/Admin/Programs'));
const AdminFAQs = lazy(() => import('../pages/Admin/FAQs'));
const AdminAnnouncements = lazy(() => import('../pages/Admin/Announcements'));
const AdminChildren = lazy(() => import('../pages/Admin/Children'));
const AdminEnrollments = lazy(() => import('../pages/Admin/Enrollments'));
const AdminOrders = lazy(() => import('../pages/Admin/Orders'));

export function AppRouter() {
  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/"
        element={
          <LazyPage>
            <Home />
          </LazyPage>
        }
        errorElement={<RouteErrorBoundary />}
      />
      <Route
        path="/programs"
        element={
          <LazyPage>
            <Programs />
          </LazyPage>
        }
        errorElement={<RouteErrorBoundary />}
      />
      <Route
        path="/programs/:id"
        element={
          <LazyPage>
            <ProgramDetail />
          </LazyPage>
        }
        errorElement={<RouteErrorBoundary />}
      />
      <Route
        path="/about"
        element={
          <LazyPage>
            <About />
          </LazyPage>
        }
        errorElement={<RouteErrorBoundary />}
      />
      <Route
        path="/faq"
        element={
          <LazyPage>
            <FAQ />
          </LazyPage>
        }
        errorElement={<RouteErrorBoundary />}
      />

      {/* Auth routes */}
      <Route
        path="/login"
        element={
          <LazyPage>
            <Login />
          </LazyPage>
        }
        errorElement={<RouteErrorBoundary />}
      />
      <Route
        path="/signup"
        element={
          <LazyPage>
            <Signup />
          </LazyPage>
        }
        errorElement={<RouteErrorBoundary />}
      />
      <Route
        path="/forgot-password"
        element={
          <LazyPage>
            <ForgotPassword />
          </LazyPage>
        }
        errorElement={<RouteErrorBoundary />}
      />

      {/* Payment success (protected) */}
      <Route
        path="/payment-success"
        element={
          <ProtectedRoute>
            <LazyPage>
              <PaymentSuccess />
            </LazyPage>
          </ProtectedRoute>
        }
        errorElement={<RouteErrorBoundary />}
      />

      {/* Dashboard routes (protected) */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <LazyPage>
              <Dashboard />
            </LazyPage>
          </ProtectedRoute>
        }
        errorElement={<RouteErrorBoundary />}
      >
        <Route
          index
          element={
            <LazyPage>
              <DashboardHome />
            </LazyPage>
          }
        />
        <Route
          path="children"
          element={
            <LazyPage>
              <DashboardChildren />
            </LazyPage>
          }
        />
        <Route
          path="enrollments"
          element={
            <LazyPage>
              <DashboardEnrollments />
            </LazyPage>
          }
        />
        <Route
          path="orders"
          element={
            <LazyPage>
              <DashboardOrders />
            </LazyPage>
          }
        />
        <Route
          path="lessons"
          element={
            <LazyPage>
              <DashboardLessons />
            </LazyPage>
          }
        />
        <Route
          path="lessons/:programId"
          element={
            <LazyPage>
              <DashboardLessonList />
            </LazyPage>
          }
        />
        <Route
          path="lessons/:programId/:lessonId"
          element={
            <LazyPage>
              <DashboardLessonViewer />
            </LazyPage>
          }
        />
      </Route>

      {/* Admin routes (protected + admin role) */}
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <LazyPage>
              <Admin />
            </LazyPage>
          </AdminRoute>
        }
        errorElement={<RouteErrorBoundary />}
      >
        <Route
          index
          element={
            <LazyPage>
              <AdminHome />
            </LazyPage>
          }
        />
        <Route
          path="users"
          element={
            <LazyPage>
              <AdminUsers />
            </LazyPage>
          }
        />
        <Route
          path="programs"
          element={
            <LazyPage>
              <AdminPrograms />
            </LazyPage>
          }
        />
        <Route
          path="faqs"
          element={
            <LazyPage>
              <AdminFAQs />
            </LazyPage>
          }
        />
        <Route
          path="announcements"
          element={
            <LazyPage>
              <AdminAnnouncements />
            </LazyPage>
          }
        />
        <Route
          path="children"
          element={
            <LazyPage>
              <AdminChildren />
            </LazyPage>
          }
        />
        <Route
          path="enrollments"
          element={
            <LazyPage>
              <AdminEnrollments />
            </LazyPage>
          }
        />
        <Route
          path="orders"
          element={
            <LazyPage>
              <AdminOrders />
            </LazyPage>
          }
        />
      </Route>

      {/* Catch-all 404 */}
      <Route
        path="*"
        element={
          <LazyPage>
            <NotFound />
          </LazyPage>
        }
      />
    </Routes>
  );
}
